/**
 * Authentication Store
 * Manages user authentication state with Zustand
 */

import { create } from 'zustand';
import { apiCall, setAuthRequiredHandler, SESSION_ENDED_MESSAGE } from '@/lib/api';
import { useUIStore } from '@/stores/uiStore';
import type { AuthUser, LoginResponse, MeResponse } from '@/types/api';

// Re-export AuthUser as User for backwards compatibility
export type User = AuthUser;

export interface RefreshOptions {
  /** Keep the current user on failure and don't toggle isLoading (background check) */
  silent?: boolean;
  /** Skip the request if the last successful check is younger than this */
  maxAgeMs?: number;
}

// A session check in flight (module scope: a promise is not React state)
let pendingCheck: Promise<void> | null = null;

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Time of the last successful session check (drives refreshUser's maxAgeMs) */
  lastSessionCheckAt: number;
  /** Bumped on logout so a late /me response cannot re-authenticate */
  sessionEpoch: number;

  // Actions
  login: (itsId: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: (options?: RefreshOptions) => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
}

/**
 * Authentication Store
 * Handles user login, logout, session management
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: false,
  lastSessionCheckAt: 0,
  sessionEpoch: 0,

  /**
   * Login user with ITS ID and password
   * Sets user and authentication state on success
   * Throws error on failure (component will catch and show toast)
   */
  login: async (itsId: string, password: string) => {
    set({ isLoading: true });

    try {
      const response = await apiCall<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ itsId, password }),
      });

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw error; // Re-throw for component to handle
    }
  },

  /**
   * Logout user
   * Clears user state and calls logout endpoint
   */
  logout: async () => {
    // A session check in flight would re-issue the cookie after logout: let it finish first
    if (pendingCheck) await pendingCheck.catch(() => {});
    set((state) => ({ sessionEpoch: state.sessionEpoch + 1 }));
    try {
      await apiCall('/api/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Continue with logout even if API call fails
    }

    // Clear local state
    set({
      user: null,
      isAuthenticated: false,
    });
  },

  /**
   * Verify the session with the server and refresh the user (also renews the cookie).
   * silent: background check, keeps state on failure. maxAgeMs: throttle.
   */
  refreshUser: async (options: RefreshOptions = {}) => {
    const { silent = false, maxAgeMs } = options;
    if (maxAgeMs !== undefined && Date.now() - get().lastSessionCheckAt < maxAgeMs) return;
    if (!silent) set({ isLoading: true });
    const epoch = get().sessionEpoch;

    const check = (async () => {
      try {
        const response = await apiCall<MeResponse>('/api/auth/me', { method: 'GET' });
        if (epoch !== get().sessionEpoch) return; // logged out meanwhile
        if (!response?.user) throw new Error('Malformed /api/auth/me response');
        set({ user: response.user, isAuthenticated: true, isLoading: false, lastSessionCheckAt: Date.now() });
      } catch (error) {
        if (epoch !== get().sessionEpoch) return;
        // AUTH_REQUIRED is handled by the handler below; anything else (network) only
        // clears the user on a non-silent check
        if (silent) set({ isLoading: false });
        else set({ user: null, isAuthenticated: false, isLoading: false });
      }
    })();
    pendingCheck = check;
    try {
      await check;
    } finally {
      if (pendingCheck === check) pendingCheck = null;
    }
  },

  /**
   * Set user directly (for testing or manual updates)
   */
  setUser: (user: User | null) => {
    set({
      user,
      isAuthenticated: user !== null,
    });
  },

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));

// Any API 401 + AUTH_REQUIRED while a user is loaded means the session died
// (expired, password changed elsewhere, account deactivated or deleted):
// clear the user so ProtectedRoute redirects to /login, and say why.
setAuthRequiredHandler(() => {
  if (!useAuthStore.getState().user) return;
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
  useUIStore.getState().showToast(SESSION_ENDED_MESSAGE, 'info');
});
